// src/pages/enabledRoles/enabledRoles.js
import React, { useCallback, useState, useEffect, useMemo } from "react";
import { Table } from "antd";
import { FaCircle } from "react-icons/fa";
import axios from "../../services/axiosConfig";
import API_ENDPOINTS from "../../services/apiEndpoints";
import { getAuthToken } from "../../auth/auth";
import useNotification from "../../hooks/useNotification";
import Layout from "../../components/layout/layout";
import TableLayout from "../../components/tableLayout/tableLayout";
import "./enabledRoles.css";

// Keep your hardcoded data as fallback
export const initialRoles = [
  {
    key: "0",
    type: "user",
    components: ["Mobile App Access Only"],
    description: "Regular church member - can only access mobile application, no admin panel access."
  },
  {
    key: "1",
    type: "head_admin",
    components: ["Full Access"],
    description: "Only head admin can create new roles and assign roles."
  },
  {
    key: "2",
    type: "associate_admin_1",
    components: ["Full Access except Donation management"],
    description: "Full access except donation management."
  },
  {
    key: "3",
    type: "secretary",
    components: ["Calendar Event Management", "Event Form Management", "Membership Form Management", "Announcement Management"],
    description: "Manages calendar events, event forms, membership forms, and announcements."
  },
  {
    key: "4",
    type: "associate_secretary",
    components: ["Calendar Event Management", "Event Form Management", "Membership Form Management", "Announcement Management"],
    description: "Assists in managing calendar events, event forms, membership forms, and announcements."
  },
  {
    key: "5",
    type: "treasurer",
    components: ["Donation Management", "Reports & Analytics"],
    description: "Handles donation management and reports & analytics."
  },
  {
    key: "6",
    type: "associate_treasurer",
    components: ["Donation Management", "Reports & Analytics"],
    description: "Assists in donation management and reports & analytics."
  },
  {
    key: "7",
    type: "prayer_lead",
    components: ["Prayer Request Management"],
    description: "Leads prayer request management."
  },
  {
    key: "8",
    type: "associate_prayer_lead",
    components: ["Prayer Request Management"],
    description: "Assists in prayer request management."
  },
  {
    key: "9",
    type: "sunday_school_lead",
    components: ["Media & Content Management", "Announcement Management"],
    description: "Leads Sunday school media & content and announcements."
  },
  {
    key: "10",
    type: "sunday_school_associate",
    components: ["Media & Content Management", "Announcement Management"],
    description: "Assists in Sunday school media & content and announcements."
  },
  {
    key: "11",
    type: "pastoral_team_1",
    components: [
      "Pastoral Chat Management",
      "Prayer Request Management",
      "Testimony Management",
      "Media Management",
      "Event Form Management",
      "Calendar Event Management",
      "Membership Form Management",
      "Announcement Management"
    ],
    description: "Pastoral team with broad management access."
  },
  {
    key: "12",
    type: "pastoral_team_2",
    components: [
      "Pastoral Chat Management",
      "Prayer Request Management",
      "Testimony Management",
      "Media Management",
      "Event Form Management",
      "Calendar Event Management",
      "Membership Form Management",
      "Announcement Management"
    ],
    description: "Pastoral team with broad management access."
  },
  {
    key: "13",
    type: "media_head",
    components: ["Media & Content Management"],
    description: "Leads media & content management."
  },
  {
    key: "14",
    type: "media_head_associate",
    components: ["Media & Content Management"],
    description: "Assists in media & content management."
  }
];

const EnabledRolesPage = () => {
  const { notification, showNotification, hideNotification } = useNotification();
  const [roles, setRoles] = useState(initialRoles); // Start with fallback data
  const [loading, setLoading] = useState(false);
  const [, setError] = useState(null);
  const [dataSource, setDataSource] = useState("fallback"); // Track data source

  // Fetch roles from DynamoDB via your API
  const fetchRoles = useCallback(
    async (showLoading = true) => {
      console.log("roleEnable.js fetchRoles");
      if (showLoading) setLoading(true);
      setError(null);

      try {
        const token = await getAuthToken();
        if (!token) {
          throw new Error("Authentication token not available");
        }

        console.log("🚀 Fetching roles from API...");

        const response = await axios.get(API_ENDPOINTS.ADMIN_ROLES, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          timeout: 15000
        });

        console.log("📊 API Response:", response.data);

        if (response.data?.success && Array.isArray(response.data.roles)) {
          setRoles(response.data.roles);
          setDataSource("database");
          showNotification(`Successfully loaded ${response.data.roles.length} roles from database`, "success", "Data Loaded");
          console.log(`✅ Successfully loaded ${response.data.roles.length} roles from DynamoDB`);
        } else {
          throw new Error("Invalid response format from API");
        }
      } catch (error) {
        console.error("❌ Error fetching roles:", error);
        setDataSource("fallback");

        // Only show error notification if it's not the initial silent load
        if (showLoading) {
          const errorMessage = error.response?.data?.message || error.message || "Failed to load roles from database";

          showNotification(`${errorMessage}. Using cached data.`, "warning", "Connection Issue");
        }

        setError(error.message);
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Load roles when component mounts
  useEffect(() => {
    fetchRoles(true);
  }, [fetchRoles]);

  // Manual refresh function
  const handleRefresh = () => {
    fetchRoles(true);
  };

  const columns = useMemo(
    () => [
      {
        title: "SL. NO.",
        render: (_, __, index) => index + 1,
        width: 80
      },
      {
        title: "ROLE TYPE",
        dataIndex: "type",
        render: (type) => <span className="role-type">{type.replace(/_/g, " ").toUpperCase()}</span>
      },
      {
        title: "ACCESSIBLE FUNCTIONS",
        dataIndex: "components",
        render: (components) => {
          let items = [];
          try {
            const parsed =
              typeof components === "string" ? JSON.parse(components) : components;
            if (Array.isArray(parsed)) {
              items = parsed.map((c) =>
                c && typeof c === "object" && "S" in c ? c.S : String(c)
              );
            }
          } catch {
            // fallback: treat as plain comma-separated string
            items =
              typeof components === "string"
                ? components.split(",").map((s) => s.trim()).filter(Boolean)
                : [];
          }
          return (
            <div className="role-functions-container">
              {items.map((comp, idx) => (
                <span key={`${comp}-${idx}`} className="role-function-tag">
                  {comp}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        title: "DESCRIPTION",
        dataIndex: "description",
        render: (description) => <span className="role-description">{description}</span>
      }
    ],
    []
  ); // Empty dependency array means this only runs once

  return (
    <Layout
      navTitle="Roles & Permissions"
      pageTitle={
        <div className="role-page-title">
          <span>View Roles</span>
          <div className="role-actions">
            <button onClick={handleRefresh} className="role-refresh-btn" disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
            <div className="role-status">
              <span className={`role-enable-data-source ${dataSource}`}>
                {dataSource === "database" ? (
                  <span>
                    <FaCircle
                      style={{
                        color: "hsl(120, 100%, 35%)",
                        padding: "6px 4px 0 2px"
                      }}
                    />{" "}
                    Live Data
                  </span>
                ) : (
                  <span>
                    <FaCircle
                      style={{
                        color: "hsl(60, 100%, 50%)",
                        padding: "6px 4px 0 2px"
                      }}
                    />{" "}
                    Standby Data
                  </span>
                )}
              </span>
              <span className="role-count">
                Total roles: <span className="role-count-number">{roles.length}</span>
              </span>
            </div>
          </div>
        </div>
      }
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <TableLayout>
        <Table className="tpl-table" rowKey={(record) => record.key || record.id} columns={columns} dataSource={roles} loading={loading} pagination={false} />
      </TableLayout>
    </Layout>
  );
};

export default EnabledRolesPage;